import React, { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import {
  Users, Loader2, Plus, Search, RefreshCw, Send, Edit3, Trash2,
  CheckCircle2, XCircle, Phone, Mail, MapPin, X, ImportIcon, Tag,
} from "lucide-react";
import { api } from "../lib/api";

const SOURCE_LABELS = {
  landing: { label: "Landing", color: "bg-amber-50 text-amber-700 border-amber-200" },
  admin: { label: "Manual", color: "bg-sky-50 text-sky-700 border-sky-200" },
  whatsapp: { label: "WhatsApp", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  migration_user: { label: "Mig. Usuários", color: "bg-violet-50 text-violet-700 border-violet-200" },
  migration_whatsapp: { label: "Mig. WhatsApp", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const DEFAULT_FORM = {
  name: "", phone: "", email: "", city: "", group: "membros",
  active: true, whatsapp_opt_in: false, notes: "",
};

function fmtPhone(p) {
  if (!p) return ", ";
  // +5511988887777 → +55 (11) 9 8888-7777
  const m = p.match(/^\+(\d{2})(\d{2})(\d{1})?(\d{4})(\d{4})$/);
  if (m) return `+${m[1]} (${m[2]}) ${m[3] || ""}${m[3] ? " " : ""}${m[4]}-${m[5]}`;
  return p;
}

function fmtRelative(iso) {
  if (!iso) return ", ";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "agora";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
}

export default function AdminContacts() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [filters, setFilters] = useState({ q: "", group: "", active: "", source: "" });
  const [editing, setEditing] = useState(null); // contact obj or "new"
  const [migrating, setMigrating] = useState(false);
  const [testing, setTesting] = useState(null);  // contact id

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.q) params.set("q", filters.q);
      if (filters.group) params.set("group", filters.group);
      if (filters.active !== "") params.set("active", filters.active);
      if (filters.source) params.set("source", filters.source);
      params.set("limit", "100");
      const r = await api.get(`/admin/contacts?${params.toString()}`);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar contatos.");
    } finally { setLoading(false); }
  }, [filters]);

  const loadGroups = useCallback(async () => {
    try {
      const r = await api.get("/admin/contacts/groups");
      setGroups(r.data.groups || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadGroups(); }, [loadGroups]);

  const runMigration = async () => {
    if (migrating) return;
    if (!window.confirm("Migrar contatos existentes (users com telefone + whatsapp_users) para a coleção contacts? A operação é idempotente, pode rodar múltiplas vezes.")) return;
    setMigrating(true);
    try {
      const r = await api.post("/admin/contacts/migrate");
      const { inserted, updated, skipped, sources } = r.data;
      toast.success(`${inserted} novos · ${updated} atualizados · ${skipped} já existentes (users: ${sources.users}, whatsapp: ${sources.whatsapp_users}).`);
      load(); loadGroups();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha na migração.");
    } finally { setMigrating(false); }
  };

  const handleSave = async (form) => {
    try {
      if (editing === "new") {
        await api.post("/admin/contacts", form);
        toast.success("Contato criado.");
      } else {
        await api.put(`/admin/contacts/${editing.id}`, form);
        toast.success("Contato atualizado.");
      }
      setEditing(null);
      load(); loadGroups();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível salvar.");
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Excluir contato "${c.name}" (${fmtPhone(c.phone)})? Isso é permanente.`)) return;
    try {
      await api.delete(`/admin/contacts/${c.id}`);
      toast.success("Contato excluído.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao excluir.");
    }
  };

  const handleTest = async (c) => {
    if (testing) return;
    const msg = window.prompt(`Enviar mensagem de teste para ${c.name} (${fmtPhone(c.phone)}):`,
      "🙏 Olá! Esta é uma mensagem de teste do app C E Medeiros. Que Deus te abençoe!");
    if (!msg || msg.trim().length < 1) return;
    setTesting(c.id);
    try {
      const r = await api.post(`/admin/contacts/${c.id}/test-message`, { message: msg });
      if (r.data.success) {
        toast.success(`Mensagem enviada (SID ${r.data.message_sid?.slice(-8)}).`);
        load();
      } else {
        toast.error(r.data.error || "Falha no envio.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no envio.");
    } finally { setTesting(null); }
  };

  return (
    <section data-testid="admin-contacts-tab" className="space-y-6">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold flex items-center gap-1.5">
            <Users className="w-3 h-3" /> Base unificada
          </p>
          <h2 className="font-display text-3xl text-navy mt-1">Contatos</h2>
          <p className="text-[13px] text-navy/55 mt-1.5 max-w-xl">
            Lista única de leads/membros para envio via WhatsApp (devocional diário, alertas, campanhas).
            Independente do sistema de login.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={runMigration}
            disabled={migrating}
            data-testid="contacts-migrate-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border text-[11px] uppercase tracking-widest text-navy/75 hover:border-gold/60 hover:text-navy disabled:opacity-50 transition-colors"
          >
            {migrating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImportIcon className="w-3.5 h-3.5" />}
            Migrar existentes
          </button>
          <button
            onClick={() => setEditing("new")}
            data-testid="contacts-new-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-navy text-white hover:bg-gold hover:text-navy text-[11px] uppercase tracking-widest transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Novo contato
          </button>
        </div>
      </header>

      {/* Filters */}
      <div className="bg-white border border-border rounded-2xl p-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-navy/40" />
          <input
            type="text"
            placeholder="Buscar nome, telefone, e-mail, cidade…"
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            data-testid="contacts-search"
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-navy/40"
          />
        </div>
        <select
          value={filters.group}
          onChange={(e) => setFilters({ ...filters, group: e.target.value })}
          data-testid="contacts-filter-group"
          className="bg-[hsl(var(--parchment))] border border-border rounded-full px-3 py-1.5 text-[12px] outline-none"
        >
          <option value="">Todos os grupos</option>
          {groups.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        <select
          value={filters.source}
          onChange={(e) => setFilters({ ...filters, source: e.target.value })}
          data-testid="contacts-filter-source"
          className="bg-[hsl(var(--parchment))] border border-border rounded-full px-3 py-1.5 text-[12px] outline-none"
        >
          <option value="">Todas as origens</option>
          <option value="landing">Landing</option>
          <option value="admin">Manual</option>
          <option value="whatsapp">WhatsApp</option>
          <option value="migration_user">Migração, Usuários</option>
          <option value="migration_whatsapp">Migração, WhatsApp</option>
        </select>
        <select
          value={filters.active}
          onChange={(e) => setFilters({ ...filters, active: e.target.value })}
          data-testid="contacts-filter-active"
          className="bg-[hsl(var(--parchment))] border border-border rounded-full px-3 py-1.5 text-[12px] outline-none"
        >
          <option value="">Todos</option>
          <option value="true">Ativos</option>
          <option value="false">Inativos</option>
        </select>
        <button
          onClick={load}
          disabled={loading}
          data-testid="contacts-refresh"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] text-navy/60 hover:text-navy"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Atualizar
        </button>
      </div>

      {/* Stats summary */}
      <div className="text-[12px] text-navy/55" data-testid="contacts-total">
        {loading ? "Carregando…" : (
          <span><strong className="text-navy">{total}</strong> contatos · <strong className="text-emerald-600">{items.filter(i => i.whatsapp_opt_in).length}</strong> com opt-in WhatsApp confirmado</span>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-border rounded-2xl overflow-hidden">
        {loading ? (
          <div className="text-center text-navy/55 py-12">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" />Carregando…
          </div>
        ) : items.length === 0 ? (
          <div className="text-center text-navy/55 py-12">
            <Users className="w-8 h-8 mx-auto mb-2 text-navy/30" />
            <p>Nenhum contato encontrado.</p>
            <p className="text-xs mt-1">Use "Migrar existentes" para importar usuários e contatos do WhatsApp.</p>
          </div>
        ) : (
          <ul className="divide-y divide-border" data-testid="contacts-list">
            {items.map((c) => {
              const meta = SOURCE_LABELS[c.source] || SOURCE_LABELS.admin;
              return (
                <li key={c.id} data-testid={`contact-row-${c.id}`} className="p-4 flex items-center gap-4 flex-wrap hover:bg-[hsl(var(--parchment))]/50 transition-colors">
                  <div className="flex-1 min-w-[180px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-navy truncate">{c.name}</p>
                      <span className={`inline-block text-[9px] uppercase tracking-widest border rounded-full px-1.5 py-0.5 ${meta.color}`}>{meta.label}</span>
                      {c.group && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-navy/55">
                          <Tag className="w-2.5 h-2.5" />{c.group}
                        </span>
                      )}
                      {!c.active && (
                        <span className="inline-block text-[9px] uppercase tracking-widest border rounded-full px-1.5 py-0.5 bg-rose-50 text-rose-700 border-rose-200">inativo</span>
                      )}
                    </div>
                    <p className="text-[12px] text-navy/55 mt-1 flex items-center gap-3 flex-wrap">
                      <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{fmtPhone(c.phone)}</span>
                      {c.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>}
                      {c.city && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{c.city}</span>}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-widest text-navy/50">Última msg</p>
                    <p className="text-[11px] text-navy/65">{fmtRelative(c.last_message_at)}</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {c.whatsapp_opt_in ? (
                      <span title="Opt-in WhatsApp confirmado"><CheckCircle2 className="w-4 h-4 text-emerald-600" /></span>
                    ) : (
                      <span title="Sem opt-in confirmado (precisa fazer join no Sandbox)"><XCircle className="w-4 h-4 text-navy/30" /></span>
                    )}
                    <button
                      onClick={() => handleTest(c)}
                      disabled={testing === c.id}
                      data-testid={`contact-test-${c.id}`}
                      title="Enviar mensagem de teste"
                      className="p-2 rounded-full hover:bg-emerald-50 text-emerald-700 disabled:opacity-50"
                    >
                      {testing === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => setEditing(c)}
                      data-testid={`contact-edit-${c.id}`}
                      title="Editar"
                      className="p-2 rounded-full hover:bg-navy/5 text-navy/70"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(c)}
                      data-testid={`contact-delete-${c.id}`}
                      title="Excluir"
                      className="p-2 rounded-full hover:bg-rose-50 text-rose-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {editing && (
        <ContactFormModal
          contact={editing === "new" ? null : editing}
          groups={groups}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}
    </section>
  );
}

function ContactFormModal({ contact, groups, onClose, onSave }) {
  const [form, setForm] = useState(() => contact ? {
    name: contact.name || "",
    phone: contact.phone || "",
    email: contact.email || "",
    city: contact.city || "",
    group: contact.group || "membros",
    active: contact.active !== false,
    whatsapp_opt_in: !!contact.whatsapp_opt_in,
    notes: contact.notes || "",
  } : { ...DEFAULT_FORM });
  const [saving, setSaving] = useState(false);

  const valid = useMemo(() =>
    form.name.trim().length >= 2 && form.phone.replace(/[^\d+]/g, "").length >= 8,
  [form]);

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || saving) return;
    setSaving(true);
    const payload = { ...form };
    if (!payload.email) delete payload.email;
    if (!payload.city) delete payload.city;
    if (!payload.notes) delete payload.notes;
    await onSave(payload);
    setSaving(false);
  };

  return (
    <div data-testid="contact-form-modal" className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-navy/60 backdrop-blur-sm">
      <div onClick={onClose} className="absolute inset-0" />
      <form
        onSubmit={submit}
        className="relative bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 m-0 sm:m-4 shadow-2xl"
      >
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{contact ? "Editar" : "Novo"}</p>
            <h3 className="font-display text-2xl text-navy">Contato</h3>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-navy/5">
            <X className="w-4 h-4 text-navy/60" />
          </button>
        </div>

        <div className="space-y-4">
          <Field label="Nome *">
            <input
              required minLength={2} maxLength={80}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="contact-form-name"
              className="w-full mt-1 h-11 px-3 bg-[hsl(var(--parchment))] border border-transparent focus:bg-white focus:border-gold/60 rounded-lg outline-none text-sm"
            />
          </Field>
          <Field label="Telefone WhatsApp * (formato +55…)">
            <input
              required
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="contact-form-phone"
              placeholder="+5581999998888 ou 81999998888"
              className="w-full mt-1 h-11 px-3 bg-[hsl(var(--parchment))] border border-transparent focus:bg-white focus:border-gold/60 rounded-lg outline-none text-sm font-mono"
            />
          </Field>
          <Field label="E-mail (opcional)">
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="contact-form-email"
              className="w-full mt-1 h-11 px-3 bg-[hsl(var(--parchment))] border border-transparent focus:bg-white focus:border-gold/60 rounded-lg outline-none text-sm"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Cidade">
              <input
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                data-testid="contact-form-city"
                className="w-full mt-1 h-11 px-3 bg-[hsl(var(--parchment))] border border-transparent focus:bg-white focus:border-gold/60 rounded-lg outline-none text-sm"
              />
            </Field>
            <Field label="Grupo">
              <input
                list="contact-groups"
                value={form.group}
                onChange={(e) => setForm({ ...form, group: e.target.value })}
                data-testid="contact-form-group"
                placeholder="membros, lideranca, ouvintes…"
                className="w-full mt-1 h-11 px-3 bg-[hsl(var(--parchment))] border border-transparent focus:bg-white focus:border-gold/60 rounded-lg outline-none text-sm"
              />
              <datalist id="contact-groups">
                {groups.map((g) => <option key={g} value={g} />)}
              </datalist>
            </Field>
          </div>
          <Field label="Notas">
            <textarea
              rows={3} maxLength={1000}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="contact-form-notes"
              className="w-full mt-1 px-3 py-2 bg-[hsl(var(--parchment))] border border-transparent focus:bg-white focus:border-gold/60 rounded-lg outline-none text-sm"
            />
          </Field>
          <div className="flex items-center gap-4 flex-wrap">
            <Toggle
              label="Ativo"
              checked={form.active}
              onChange={(v) => setForm({ ...form, active: v })}
              data-testid="contact-form-active"
            />
            <Toggle
              label="Opt-in WhatsApp confirmado"
              checked={form.whatsapp_opt_in}
              onChange={(v) => setForm({ ...form, whatsapp_opt_in: v })}
              data-testid="contact-form-optin"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-6">
          <button
            type="button" onClick={onClose}
            className="px-4 py-2 rounded-full text-[11px] uppercase tracking-widest text-navy/60 hover:text-navy"
          >Cancelar</button>
          <button
            type="submit" disabled={!valid || saving}
            data-testid="contact-form-save"
            className="px-5 py-2 rounded-full bg-navy text-white hover:bg-gold hover:text-navy text-[11px] uppercase tracking-widest disabled:opacity-50"
          >{saving ? "Salvando…" : "Salvar"}</button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-navy/55">{label}</span>
      {children}
    </label>
  );
}

function Toggle({ label, checked, onChange, ...props }) {
  return (
    <button
      type="button" role="switch" aria-checked={checked}
      onClick={() => onChange(!checked)}
      {...props}
      className="inline-flex items-center gap-2"
    >
      <span className={`relative inline-flex items-center h-6 w-11 rounded-full transition-colors ${checked ? "bg-emerald-600" : "bg-navy/20"}`}>
        <span className={`inline-block w-4 h-4 bg-white rounded-full shadow transition-transform ${checked ? "translate-x-6" : "translate-x-1"}`} />
      </span>
      <span className="text-[12px] text-navy/75">{label}</span>
    </button>
  );
}
